import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '12px 12px',
  verticalAlign: 'middle',
};

const MiniBar = ({ done, total, color }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ flex: 1, background: '#e2e8f0', borderRadius: '4px', height: '8px', minWidth: '56px' }}>
        <div
          style={{
            width: `${pct}%`,
            background: color,
            borderRadius: '4px',
            height: '100%',
            transition: 'width 0.3s',
          }}
        />
      </div>
      <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
        {done}/{total}
      </span>
    </div>
  );
};

const SORT_FIELDS = {
  name: (a, b) => a.name.localeCompare(b.name),
  template: (a, b) => (a.templateName || '').localeCompare(b.templateName || ''),
  pct: (a, b) => {
    const ap = a.totalTasks > 0 ? a.totalDone / a.totalTasks : 0;
    const bp = b.totalTasks > 0 ? b.totalDone / b.totalTasks : 0;
    return ap - bp;
  },
  lastActivity: (a, b) => {
    const at = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
    const bt = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
    return at - bt;
  },
};

const AdminChecklistProgress = () => {
  const history = useHistory();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    axios
      .get(`${url}/admin/checklist-progress`, { headers: getAuthHeader() })
      .then(res => setPlayers(res.data.players || []))
      .catch(() => setError('Failed to load checklist progress.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = field => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = field => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const filtered = players
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const cmp = SORT_FIELDS[sortField] ? SORT_FIELDS[sortField](a, b) : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const assignedCount = players.filter(p => p.hasAssignment).length;
  const totalTasks = players.reduce((s, p) => s + p.totalTasks, 0);
  const totalDone = players.reduce((s, p) => s + p.totalDone, 0);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1200px' }}>
      <h2 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
        Checklist Progress
      </h2>
      <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>
        {players.length} registered player{players.length !== 1 ? 's' : ''} &mdash;{' '}
        {assignedCount} with template assigned &mdash;{' '}
        {totalTasks > 0 ? Math.round((totalDone / totalTasks) * 100) : 0}% overall completion
      </p>

      <input
        type="text"
        placeholder="Search by name..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          padding: '8px 12px',
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          marginBottom: '16px',
          width: '260px',
          fontSize: '14px',
          outline: 'none',
        }}
      />

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th
                style={thStyle}
                onClick={() => handleSort('name')}
              >
                Player{sortIndicator('name')}
              </th>
              <th
                style={thStyle}
                onClick={() => handleSort('template')}
              >
                Template{sortIndicator('template')}
              </th>
              <th style={{ ...thStyle, cursor: 'default' }}>Step 1</th>
              <th style={{ ...thStyle, cursor: 'default' }}>Step 2</th>
              <th style={{ ...thStyle, cursor: 'default' }}>Step 3</th>
              <th
                style={thStyle}
                onClick={() => handleSort('pct')}
              >
                Overall{sortIndicator('pct')}
              </th>
              <th
                style={thStyle}
                onClick={() => handleSort('lastActivity')}
              >
                Last Activity{sortIndicator('lastActivity')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const pct = p.totalTasks > 0 ? Math.round((p.totalDone / p.totalTasks) * 100) : 0;
              const overallColor = pct === 100 ? '#16a34a' : pct >= 50 ? '#2e496b' : '#94a3b8';
              return (
                <tr
                  key={p.linkedPlayerId}
                  onClick={() => history.push(`/available-players/${p.linkedPlayerId}?tab=checklist`)}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                >
                  <td style={{ ...tdStyle, fontWeight: 500, color: '#1e293b' }}>
                    {p.name || '—'}
                  </td>
                  <td style={tdStyle}>
                    {p.templateName ? (
                      <span
                        style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.templateName}
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>Not assigned</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <MiniBar
                      done={p.steps[1] ? p.steps[1].done : 0}
                      total={p.steps[1] ? p.steps[1].total : 0}
                      color="#2e496b"
                    />
                  </td>
                  <td style={tdStyle}>
                    <MiniBar
                      done={p.steps[2] ? p.steps[2].done : 0}
                      total={p.steps[2] ? p.steps[2].total : 0}
                      color="#0369a1"
                    />
                  </td>
                  <td style={tdStyle}>
                    <MiniBar
                      done={p.steps[3] ? p.steps[3].done : 0}
                      total={p.steps[3] ? p.steps[3].total : 0}
                      color="#0f766e"
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          flex: 1,
                          background: '#e2e8f0',
                          borderRadius: '4px',
                          height: '10px',
                          minWidth: '70px',
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            background: overallColor,
                            borderRadius: '4px',
                            height: '100%',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#334155',
                          minWidth: '38px',
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, color: '#64748b', fontSize: '13px' }}>
                    {p.lastActivity
                      ? new Date(p.lastActivity).toLocaleDateString('pl-PL')
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}
                >
                  {search ? 'No players match your search.' : 'No registered players yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminChecklistProgress;
